import React, { useState, useEffect, useMemo } from "react";
import { apiFetch } from "../../services/api";
import { getEntityTypes } from "../../utils/entityTypes";
import {
  isTaxonomyHierarchyType,
  normalizeTaxonomyEntityType,
} from "../../utils/taxonomyHierarchyConfigs";
import { apiJsonOrThrow } from "./adminUtils";
import LoadingButton from "../common/LoadingButton";
import { createDevConsole } from "../../utils/devLogger";
import { useV2Theme } from "../../v2/theme/ThemeProvider";
import { v2cx } from "../../v2/theme/tokens";

const console = createDevConsole(process.env.NODE_ENV === "development");
const debugLog = (...args) => console.log(...args);
const debugWarn = (...args) => console.warn(...args);
const debugError = (...args) => console.error(...args);

/** Types with dedicated Taxonomies hierarchy import, or retired / special-case create paths. */
const EXCLUDE_FROM_SPREADSHEET_IMPORT = new Set([
  "interface",
  "solution-component",
]);

const normalizeImportEntityType = (type) =>
  normalizeTaxonomyEntityType(type).replace(/[^a-z0-9-]/g, "-");

const isExcludedFromSpreadsheetImport = (type) => {
  const normalized = normalizeImportEntityType(type);
  if (!normalized) return true;
  if (EXCLUDE_FROM_SPREADSHEET_IMPORT.has(normalized)) return true;
  if (isTaxonomyHierarchyType(normalized)) return true;
  return false;
};

function getImportWizardStyles(embedded, tokens) {
  if (embedded) {
    return {
      backdrop:
        "fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4",
      panel:
        "bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6",
      title: "text-2xl font-semibold text-slate-900",
      closeBtn: "text-slate-500 hover:text-slate-800 transition-colors",
      stepActive: "bg-blue-700 text-white",
      stepInactive: "bg-slate-100 text-slate-500 border border-slate-200",
      stepLabel: "mt-2 text-xs text-slate-500 whitespace-nowrap",
      stepLineActive: "bg-blue-700",
      stepLineInactive: "bg-slate-200",
      stepTitle: "text-lg font-semibold text-slate-900 mb-4",
      label: "block text-sm font-medium text-slate-700 mb-2",
      required: "text-rose-600",
      select:
        "w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500",
      subheading: "text-md font-semibold text-slate-700 mb-3",
      attrSelected: "border-blue-500 bg-blue-50",
      attrUnselected: "border-slate-200 bg-slate-50",
      attrHover: "cursor-pointer hover:bg-slate-100",
      attrDisabled: "opacity-75",
      attrText: "text-slate-900 font-medium",
      attrMeta: "text-xs text-slate-500 ml-2",
      attrDesc: "text-xs text-slate-400 mt-1",
      muted: "text-center py-4 text-slate-500",
      btnPrimary: v2cx(
        tokens.button.primary,
        "disabled:opacity-50 disabled:cursor-not-allowed"
      ),
      btnSecondary: tokens.button.secondary,
      btnDanger: tokens.button.danger,
      btnWarning: tokens.button.warning,
      btnSmPrimary: v2cx(tokens.button.primary, "text-xs"),
      btnSmSecondary: v2cx(tokens.button.secondary, "text-xs"),
      btnSmDanger: v2cx(tokens.button.danger, "text-xs"),
      btnSmWarning: v2cx(tokens.button.warning, "text-xs"),
      alertError: "mb-4 p-3 border border-rose-200 bg-rose-50 rounded-lg",
      alertErrorText: "text-sm text-rose-800",
      alertInfo: "mb-4 p-4 border border-blue-200 bg-blue-50 rounded-lg",
      alertInfoTitle: "text-sm font-semibold text-blue-900 mb-2",
      alertInfoText: "text-xs text-slate-600",
      alertSuccess: "p-4 border border-emerald-200 bg-emerald-50 rounded-lg mb-4",
      alertSuccessText: "text-sm text-emerald-800",
      alertWarning: "p-3 border border-amber-200 bg-amber-50 rounded-lg mb-4",
      alertWarningText: "text-sm text-amber-900",
      statusPanel: "mt-4 p-4 border border-slate-200 bg-slate-50 rounded-lg",
      statusTitle: "text-sm font-semibold text-slate-900",
      statusMeta: "text-xs text-slate-500",
      statusText: "text-sm text-slate-700",
      statusSubtext: "text-xs text-slate-500",
      statusSuccess: "text-sm text-emerald-700",
      statusError: "text-sm text-rose-700",
      statusDivider: "mt-3 pt-3 border-t border-slate-200",
      spinner: "animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600",
      resultTotal: "p-4 border border-blue-200 bg-blue-50 rounded-lg",
      resultTotalText: "text-slate-900 font-semibold",
      resultSuccess: "p-4 border border-emerald-200 bg-emerald-50 rounded-lg",
      resultSuccessText: "text-emerald-800 font-semibold",
      resultFailed: "p-4 border border-rose-200 bg-rose-50 rounded-lg",
      resultFailedText: "text-rose-800 font-semibold",
      resultErrorItem: "text-xs text-rose-700",
      selectedFile: "mt-2 text-sm text-slate-500",
    };
  }

  return {
    backdrop: "fixed inset-0 bg-black/60 flex items-center justify-center z-[60]",
    panel:
      "bg-gray-800 border border-gray-700 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6",
    title: "text-2xl font-semibold text-white",
    closeBtn: "text-gray-400 hover:text-white transition-colors",
    stepActive: "bg-blue-600 text-white",
    stepInactive: "bg-gray-700 text-gray-400",
    stepLabel: "mt-2 text-xs text-gray-400 whitespace-nowrap",
    stepLineActive: "bg-blue-600",
    stepLineInactive: "bg-gray-700",
    stepTitle: "text-lg font-semibold text-white mb-4",
    label: "block text-sm font-medium text-gray-300 mb-2",
    required: "text-red-400",
    select:
      "w-full bg-gray-700 text-white p-3 rounded border border-gray-600 focus:border-blue-500 focus:outline-none",
    subheading: "text-md font-semibold text-gray-300 mb-3",
    attrSelected: "border-blue-600 bg-blue-600/20",
    attrUnselected: "border-gray-600 bg-gray-700/50",
    attrHover: "cursor-pointer hover:bg-gray-700",
    attrDisabled: "opacity-75",
    attrText: "text-white font-medium",
    attrMeta: "text-xs text-gray-400 ml-2",
    attrDesc: "text-xs text-gray-500 mt-1",
    muted: "text-center py-4 text-gray-400",
    btnPrimary:
      "px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors",
    btnSecondary:
      "px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors",
    btnDanger:
      "px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors",
    btnWarning:
      "px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors",
    btnSmPrimary:
      "px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors",
    btnSmSecondary:
      "px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors",
    btnSmDanger:
      "px-3 py-1.5 text-xs bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors",
    btnSmWarning:
      "px-3 py-1.5 text-xs bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors",
    alertError: "mb-4 p-3 bg-red-900/20 border border-red-700/50 rounded-lg",
    alertErrorText: "text-sm text-red-200",
    alertInfo: "mb-4 p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg",
    alertInfoTitle: "text-sm font-semibold text-blue-200 mb-2",
    alertInfoText: "text-xs text-gray-300",
    alertSuccess: "p-4 bg-green-900/20 border border-green-700/50 rounded-lg mb-4",
    alertSuccessText: "text-sm text-green-200",
    alertWarning: "p-3 bg-yellow-900/20 border border-yellow-700/50 rounded-lg mb-4",
    alertWarningText: "text-sm text-yellow-200",
    statusPanel: "mt-4 p-4 bg-gray-800 border border-gray-700 rounded-lg",
    statusTitle: "text-sm font-semibold text-white",
    statusMeta: "text-xs text-gray-400",
    statusText: "text-sm text-gray-300",
    statusSubtext: "text-xs text-gray-400",
    statusSuccess: "text-sm text-green-400",
    statusError: "text-sm text-red-400",
    statusDivider: "mt-3 pt-3 border-t border-gray-700",
    spinner: "animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500",
    resultTotal: "p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg",
    resultTotalText: "text-white font-semibold",
    resultSuccess: "p-4 bg-green-900/20 border border-green-700/50 rounded-lg",
    resultSuccessText: "text-green-200 font-semibold",
    resultFailed: "p-4 bg-red-900/20 border border-red-700/50 rounded-lg",
    resultFailedText: "text-red-200 font-semibold",
    resultErrorItem: "text-xs text-red-300",
    selectedFile: "mt-2 text-sm text-gray-400",
  };
}

function importWizardCopy(embedded) {
  if (!embedded) {
    return {
      title: "Import Entities",
      stepItemType: "Entity Type",
      step1Title: "Step 1: Select Entity Type",
      itemTypeLabel: "Entity Type",
      selectPlaceholder: "-- Select entity type --",
      failedLoadTypes: "Failed to load entity types",
      selectTypeError: "Please select import type and entity type",
      selectTypePrompt:
        "Please select an entity type. You can go back to step 2 to select it, or use a saved configuration.",
      typePrefix: "Entity Type:",
      noCustomAttrs: "No custom attributes defined for this entity type",
      noTypeSelected:
        'No entity type selected. Click "Use Saved Config" to restore your configuration.',
      hierarchyImportNote:
        "Organisation, Objective, Initiative, Business Context, and Business Capability are imported from the Hierarchies section, not this wizard.",
      fieldFormatNote:
        "Dates use YYYY-MM-DD. Dropdown fields must use an allowed value. Multi-select values are separated with semicolons (;). Booleans are TRUE or FALSE. The downloaded template includes an Instructions sheet, a Field Guide of allowed values, and Excel dropdowns where applicable.",
      templateReadyNote:
        "Template ready. Open the Instructions and Field Guide sheets first, replace the yellow example row, then upload the populated Entity Import sheet.",
      importButton: "Import Entities",
      backgroundReady:
        "Import is running in the background. You can close this dialog and continue working; results will appear in Architecture Explorer when ready.",
      backgroundQueued:
        "Import job queued. AI is processing in the background. Results will appear in Architecture Explorer when processing completes.",
    };
  }
  return {
    title: "Import from spreadsheet",
    stepItemType: "Item type",
    step1Title: "Step 1: Select item type",
    itemTypeLabel: "Item type",
    selectPlaceholder: "-- Select item type --",
    failedLoadTypes: "Failed to load item types",
    selectTypeError: "Please select an item type",
    selectTypePrompt:
      "Please select an item type. Go back to step 1 to choose one, or use a saved configuration.",
    typePrefix: "Item type:",
    noCustomAttrs: "No custom fields defined for this item type",
    noTypeSelected:
      'No item type selected. Click "Use Saved Config" to restore your configuration.',
    hierarchyImportNote:
      "Organisation, Objective, Initiative, Business Context, and Business Capability are imported from the Hierarchies section, not this wizard.",
    fieldFormatNote:
      "Dates use YYYY-MM-DD. Dropdown fields must use an allowed value. Multi-select values are separated with semicolons (;). Booleans are TRUE or FALSE. The downloaded template includes an Instructions sheet, a Field Guide of allowed values, and Excel dropdowns where applicable.",
    templateReadyNote:
      "Template ready. Open the Instructions and Field Guide sheets first, replace the yellow example row, then upload the populated Entity Import sheet.",
    importButton: "Import items",
    backgroundReady:
      "Import is running in the background. You can close this dialog — new items will appear on your map when ready.",
    backgroundQueued:
      "Import queued. Processing runs in the background — check Your map when it finishes.",
  };
}

const EntityImportWizard = ({ onClose, variant = "embedded" }) => {
  const embedded = variant === "embedded";
  const copy = importWizardCopy(embedded);
  const { tokens } = useV2Theme();
  const styles = useMemo(
    () => getImportWizardStyles(embedded, tokens),
    [embedded, tokens]
  );
  const [step, setStep] = useState(1); // Step 1: Entity Type Selection (removed import type selection)
  const [importType, setImportType] = useState("xls"); // Only Excel format supported
  const [selectedEntityType, setSelectedEntityType] = useState("");
  const [entityTypesOptions, setEntityTypesOptions] = useState([]);
  const [standardAttributes, setStandardAttributes] = useState([]);
  const [customAttributes, setCustomAttributes] = useState([]);
  const [selectedStandardAttrs, setSelectedStandardAttrs] = useState(new Set());
  const [selectedCustomAttrs, setSelectedCustomAttrs] = useState(new Set());
  const [templateGenerated, setTemplateGenerated] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importResults, setImportResults] = useState(null);
  const [loading, setLoading] = useState(false); // Loading state for import processing
  const [loadingAttributes, setLoadingAttributes] = useState(false); // Separate loading state for attribute loading
  const [loadingDownload, setLoadingDownload] = useState(false); // Separate loading state for template download
  const [error, setError] = useState("");
  const [savedConfig, setSavedConfig] = useState(null);
  const [showConfigRestore, setShowConfigRestore] = useState(false);
  const [importJobId, setImportJobId] = useState(null);
  const [importStatus, setImportStatus] = useState(null);
  const [pollingTimeoutId, setPollingTimeoutId] = useState(null);

  const clearPollingTimeout = () => {
    if (pollingTimeoutId) {
      clearTimeout(pollingTimeoutId);
      setPollingTimeoutId(null);
    }
  };

  const clearSavedImportConfig = () => {
    try {
      localStorage.removeItem("entity_import_config");
      setSavedConfig(null);
    } catch (error) {
      debugWarn("Failed to clear saved config:", error);
    }
  };

  const resetImportRuntimeState = () => {
    clearPollingTimeout();
    setLoading(false);
    setLoadingAttributes(false);
    setLoadingDownload(false);
    setImportJobId(null);
    setImportStatus(null);
  };

  const applySavedConfigState = ({ jumpToUploadStep = false } = {}) => {
    if (!savedConfig) return;
    resetImportRuntimeState();
    setImportType(savedConfig.importType);
    setSelectedEntityType(savedConfig.entityType);
    setSelectedStandardAttrs(new Set(savedConfig.standardAttrs));
    setSelectedCustomAttrs(new Set(savedConfig.customAttrs));
    setShowConfigRestore(false);
    if (savedConfig.entityType) {
      loadCustomAttributes();
    }
    if (jumpToUploadStep) {
      setStep(3);
    }
  };

  // Standard entity attributes
  const STANDARD_ATTRIBUTES = [
    { key: "name", label: "Name", required: true, type: "text" },
    { key: "description", label: "Description", required: false, type: "text" },
  ];

  // Load entity types on mount and check for saved configuration
  useEffect(() => {
    const loadEntityTypes = async () => {
      try {
        const types = await getEntityTypes();
        // Exclude taxonomy hierarchy types (imported under Taxonomies → Hierarchies),
        // interfaces (need provider/consumer endpoints), and retired solution-component.
        const filteredTypes = types.filter((type) => {
          const value = typeof type === "string" ? type : type.value;
          return !isExcludedFromSpreadsheetImport(value);
        });
        setEntityTypesOptions(filteredTypes);
      } catch (error) {
        debugError("Error loading entity types:", error);
        setError(copy.failedLoadTypes);
      }
    };
    loadEntityTypes();

    // Check for saved import configuration
    try {
      const saved = localStorage.getItem("entity_import_config");
      if (saved) {
        const config = JSON.parse(saved);
        setSavedConfig(config);
        setShowConfigRestore(true);
      }
    } catch (error) {
      debugError("Error loading saved import config:", error);
    }
  }, []);

  // Load custom attributes whenever an entity type is selected
  useEffect(() => {
    if (selectedEntityType) {
      loadCustomAttributes();
    } else {
      setCustomAttributes([]);
      setSelectedCustomAttrs(new Set());
    }
  }, [selectedEntityType]);

  // Auto-select required attributes
  useEffect(() => {
    const required = new Set();
    STANDARD_ATTRIBUTES.forEach((attr) => {
      if (attr.required) {
        required.add(attr.key);
      }
    });
    setSelectedStandardAttrs(required);
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingTimeoutId) {
        clearTimeout(pollingTimeoutId);
      }
    };
  }, [pollingTimeoutId]);

  const loadCustomAttributes = async () => {
    try {
      setLoadingAttributes(true); // Use separate loading state for attributes
      setError("");

      // Normalize entity type name to match database format
      // The selectedEntityType is the value from TYPE_PROFILE (e.g., "application", "business-capability")
      const normalizedTypeName = selectedEntityType
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, "-");

      debugLog(
        "Loading custom attributes for entity type:",
        selectedEntityType,
        "normalized:",
        normalizedTypeName
      );

      const data = await apiJsonOrThrow(
        `/api/admin/entity-type-attributes?entity_type_name=${encodeURIComponent(normalizedTypeName)}`
      );
      debugLog("Custom attributes response:", data);

        const activeAttrs = Array.isArray(data)
          ? data.filter((attr) => attr.is_active !== false)
          : [];

        // Filter out excluded attributes for Application entity type
        const excludedAttributeNames = [
          "active_start_date",
          "end_of_life_start_date",
          "phase_in_start_date",
          "phase_out_start_date",
          "plan_start_date",
          "functional_fitness",
          "technical_fitness",
          "business_criticality",
          "time",
          "monthly_cost",
        ];

        // Also check attribute labels for variations (case-insensitive)
        const excludedAttributeLabels = [
          "Active Start Date",
          "End of Life Start Date",
          "Phase In Start Date",
          "Phase Out Start Date",
          "Plan Start Date",
          "Functional Fitness",
          "Technical Fitness",
          "Business Criticality",
          "TIME",
          "Monthly Cost",
        ];

        let filteredAttrs = activeAttrs;
        if (normalizedTypeName === "application") {
          filteredAttrs = activeAttrs.filter((attr) => {
            const attrName = (attr.attribute_name || "")
              .toLowerCase()
              .replace(/[^a-z0-9_]/g, "_");
            const attrLabel = (attr.attribute_label || "").trim();
            const attrLabelLower = attrLabel.toLowerCase();

            // Check if attribute name matches any excluded attribute (exact or contains)
            const isExcludedByName = excludedAttributeNames.some((excluded) => {
              const normalizedExcluded = excluded.toLowerCase();
              return (
                attrName === normalizedExcluded ||
                attrName.includes(normalizedExcluded)
              );
            });

            // Check if attribute label matches any excluded attribute (case-insensitive, exact or contains)
            const isExcludedByLabel = excludedAttributeLabels.some(
              (excluded) => {
                const excludedLower = excluded.toLowerCase();
                return (
                  attrLabelLower === excludedLower ||
                  attrLabelLower.includes(excludedLower)
                );
              }
            );

            return !isExcludedByName && !isExcludedByLabel;
          });
        }

        debugLog("Filtered custom attributes:", filteredAttrs);
        setCustomAttributes(filteredAttrs);

        // Auto-select required custom attributes (only from filtered list)
        const required = new Set();
        filteredAttrs.forEach((attr) => {
          if (attr.is_required) {
            required.add(attr.attribute_name);
          }
        });
        setSelectedCustomAttrs(required);
    } catch (error) {
      debugError("Error loading custom attributes:", error);
      setCustomAttributes([]);
      // Don't set error state here - it's not critical if there are no custom attributes
    } finally {
      setLoadingAttributes(false); // Use separate loading state for attributes
    }
  };

  const handleStandardAttrToggle = (attrKey) => {
    const newSet = new Set(selectedStandardAttrs);
    const attr = STANDARD_ATTRIBUTES.find((a) => a.key === attrKey);

    // Don't allow deselecting required attributes
    if (attr && attr.required) {
      return;
    }

    if (newSet.has(attrKey)) {
      newSet.delete(attrKey);
    } else {
      newSet.add(attrKey);
    }
    setSelectedStandardAttrs(newSet);
  };

  const handleCustomAttrToggle = (attrName) => {
    const newSet = new Set(selectedCustomAttrs);
    const attr = customAttributes.find((a) => a.attribute_name === attrName);

    // Don't allow deselecting required attributes
    if (attr && attr.is_required) {
      return;
    }

    if (newSet.has(attrName)) {
      newSet.delete(attrName);
    } else {
      newSet.add(attrName);
    }
    setSelectedCustomAttrs(newSet);
  };

  const downloadTemplateFile = async (payload) => {
    const response = await apiFetch("/api/admin/entities/import/template", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || "Failed to download template");
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "entity_import_template.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const generateTemplate = async () => {
    if (!importType || !selectedEntityType) {
      setError(copy.selectTypeError);
      return;
    }

    if (selectedStandardAttrs.size === 0 && selectedCustomAttrs.size === 0) {
      setError("Please select at least one attribute");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const selectedAttrs = {
        standard: Array.from(selectedStandardAttrs),
        custom: Array.from(selectedCustomAttrs),
      };

      await downloadTemplateFile({
        import_type: importType,
        entity_type: selectedEntityType,
        attributes: selectedAttrs,
      });

      // Save configuration to localStorage for later use
      const config = {
        importType,
        entityType: selectedEntityType,
        standardAttrs: Array.from(selectedStandardAttrs),
        customAttrs: Array.from(selectedCustomAttrs),
        timestamp: new Date().toISOString(),
      };
      try {
        localStorage.setItem("entity_import_config", JSON.stringify(config));
        setSavedConfig(config);
      } catch (error) {
        debugWarn("Failed to save import configuration:", error);
      }

      setTemplateGenerated(true);
      // Don't auto-advance to step 4 - let user work on template and come back
      setStep(2); // Stay on step 2 (attribute selection) to show success message
    } catch (error) {
      debugError("Error generating template:", error);
      setError("Failed to generate template: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setError("Please upload an XLS or XLSX file");
      return;
    }

    setUploadedFile(file);
    setError("");
  };

  const pollImportJobStatus = async (jobId) => {
    const maxAttempts = 300; // Poll for up to 5 minutes (300 * 1 second)
    let attempts = 0;
    const finishPollingWithError = (message) => {
      setError(message);
      setLoading(false);
      setImportJobId(null);
      setPollingTimeoutId(null);
    };
    const scheduleNextPoll = () => {
      attempts++;
      if (attempts < maxAttempts) {
        const timeoutId = setTimeout(poll, 1000); // Poll every second
        setPollingTimeoutId(timeoutId);
        return;
      }
      finishPollingWithError(
        "Import is taking longer than expected. Please check back later."
      );
    };

    const poll = async () => {
      try {
        const response = await apiFetch(`/api/jobs/${jobId}`);
        if (response.ok) {
          const job = await response.json();
          setImportStatus(job);

          if (job.status === "completed") {
            // Import completed successfully
            if (job.result) {
              setImportResults(job.result);
              setStep(4);
              clearSavedImportConfig();
            }
            setLoading(false);
            setImportJobId(null);
            return;
          } else if (job.status === "failed") {
            // Import failed
            setError(job.error || "Import failed");
            setLoading(false);
            setImportJobId(null);
            return;
          } else if (job.status === "running" || job.status === "pending") {
            // Still processing, continue polling
            scheduleNextPoll();
          }
        } else {
          // Error fetching job status
          if (response.status === 404) {
            // Job not found - stop polling immediately
            debugLog(
              `⚠️ Import job ${jobId} not found (404), stopping polling`
            );
            setError(
              "Import job not found. The job may have been deleted or expired."
            );
            setLoading(false);
            setImportJobId(null);
            setPollingTimeoutId(null);
            return;
          }
          attempts++;
          if (attempts < maxAttempts) {
            const timeoutId = setTimeout(poll, 1000);
            setPollingTimeoutId(timeoutId);
          } else {
            finishPollingWithError(
              "Failed to check import status. Please check back later."
            );
          }
        }
      } catch (error) {
        debugError("Error polling import job status:", error);
        attempts++;
        if (attempts < maxAttempts) {
          const timeoutId = setTimeout(poll, 1000);
          setPollingTimeoutId(timeoutId);
        } else {
          finishPollingWithError("Failed to check import status: " + error.message);
        }
      }
    };

    poll();
  };

  const processImport = async () => {
    if (!uploadedFile) {
      setError("Please select a file to upload");
      return;
    }

    // If no entity type is selected, try to use saved config
    if (!selectedEntityType && savedConfig) {
      useSavedConfigForUpload();
      // Wait a moment for state to update
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    if (!selectedEntityType) {
      setError(copy.selectTypePrompt);
      return;
    }

    try {
      setLoading(true);
      setError("");
      setUploadProgress(0);
      setImportStatus(null);

      const formData = new FormData();
      formData.append("file", uploadedFile);
      formData.append("entity_type", selectedEntityType);
      formData.append(
        "attributes",
        JSON.stringify({
          standard: Array.from(selectedStandardAttrs),
          custom: Array.from(selectedCustomAttrs),
        })
      );

      const result = await apiJsonOrThrow("/api/admin/entities/import/process", {
        method: "POST",
        body: formData,
        timeout: 10000, // 10 seconds should be enough to queue the job (file upload + queuing)
      });
      if (result.job_id) {
        // Job queued successfully, start polling
        setImportJobId(result.job_id);
        setImportStatus({
          status: "queued",
          message:
            result.message ||
            copy.backgroundReady,
        });
        setError(""); // Clear any previous errors
        setLoading(false); // Stop loading spinner - job is queued
        pollImportJobStatus(result.job_id);
      } else {
        // Legacy response format (shouldn't happen with new async code)
        setImportResults(result);
        setStep(4);
        setLoading(false);
      }
    } catch (error) {
      debugError("Error processing import:", error);

      // If we get a timeout, assume the job was queued (backend processes async)
      if (error.message && error.message.includes("timeout")) {
        setError("");
        setImportStatus({
          status: "queued",
          message:
            copy.backgroundQueued,
        });
        setLoading(false);
        // Note: We don't have a job_id, so we can't poll, but we'll show the queued message
        return; // Exit early - don't show error state
      }

      setError("Failed to queue import: " + error.message);
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const resetWizard = () => {
    resetImportRuntimeState();

    setStep(1); // Reset to entity type selection
    setImportType("xls"); // Always Excel
    setSelectedEntityType("");
    setSelectedStandardAttrs(new Set(["name"]));
    setSelectedCustomAttrs(new Set());
    setTemplateGenerated(false);
    setUploadedFile(null);
    setImportResults(null);
    setError("");
    setSavedConfig(null);
    setShowConfigRestore(false);
    clearSavedImportConfig();
  };

  const restoreSavedConfig = () => {
    applySavedConfigState({ jumpToUploadStep: true });
  };

  const useSavedConfigForUpload = () => {
    applySavedConfigState();
  };

  return (
    <div className={styles.backdrop}>
      <div className={styles.panel}>
        <div className="flex items-center justify-between mb-6">
          <h2 className={styles.title}>{copy.title}</h2>
          <button
            type="button"
            onClick={onClose}
            className={styles.closeBtn}
            aria-label="Close import wizard"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Progress Steps */}
        <div className="mb-6">
          <div className="flex items-start">
            {[1, 2, 3, 4].map((s) => (
              <React.Fragment key={s}>
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                      step >= s ? styles.stepActive : styles.stepInactive
                    }`}
                  >
                    {s}
                  </div>
                  <span className={styles.stepLabel}>
                    {s === 1 && copy.stepItemType}
                    {s === 2 && "Attributes"}
                    {s === 3 && "Upload"}
                    {s === 4 && "Results"}
                  </span>
                </div>
                {s < 4 && (
                  <div
                    className={`flex-1 h-1 mx-2 mt-4 ${
                      step > s ? styles.stepLineActive : styles.stepLineInactive
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Saved Configuration Restore Dialog */}
        {showConfigRestore && savedConfig && (
          <div className={styles.alertInfo}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className={styles.alertInfoTitle}>
                  Saved Import Configuration Found
                </h4>
                <p className={styles.alertInfoText}>
                  {copy.typePrefix}{" "}
                  <span className="font-semibold">
                    {savedConfig.entityType}
                  </span>
                  <br />
                  Import Type:{" "}
                  <span className="font-semibold">
                    {savedConfig.importType.toUpperCase()}
                  </span>
                  <br />
                  Saved: {new Date(savedConfig.timestamp).toLocaleString()}
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    type="button"
                    onClick={restoreSavedConfig}
                    className={styles.btnSmPrimary}
                  >
                    Use Saved Config & Upload
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowConfigRestore(false);
                      clearSavedImportConfig();
                    }}
                    className={styles.btnSmSecondary}
                  >
                    Start Fresh
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowConfigRestore(false)}
                className={v2cx(styles.closeBtn, "ml-2")}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className={styles.alertError}>
            <p className={styles.alertErrorText}>{error}</p>
          </div>
        )}

        {/* Step 1: Entity Type Selection */}
        {step === 1 && (
          <div className="space-y-4">
            <h3 className={styles.stepTitle}>{copy.step1Title}</h3>
            <div className={styles.alertInfo}>
              <p className={styles.alertInfoText}>{copy.hierarchyImportNote}</p>
            </div>
            <div>
              <label className={styles.label}>
                {copy.itemTypeLabel} <span className={styles.required}>*</span>
              </label>
              <select
                value={selectedEntityType}
                onChange={(e) => setSelectedEntityType(e.target.value)}
                className={styles.select}
              >
                <option value="">{copy.selectPlaceholder}</option>
                {entityTypesOptions.map((type) => {
                  const value = typeof type === "string" ? type : type.value;
                  const label =
                    typeof type === "string"
                      ? type.replace(/([A-Z])/g, " $1").trim()
                      : type.label;
                  return (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => selectedEntityType && setStep(2)}
                disabled={!selectedEntityType}
                className={styles.btnPrimary}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Attribute Selection */}
        {step === 2 && (
          <div className="space-y-4">
            <h3 className={styles.stepTitle}>Step 2: Select Attributes to Import</h3>

            {/* Standard Attributes */}
            <div className="mb-6">
              <h4 className={styles.subheading}>Standard Properties</h4>
              <div className="space-y-2">
                {STANDARD_ATTRIBUTES.map((attr) => (
                  <label
                    key={attr.key}
                    className={`flex items-center p-3 rounded-lg border ${
                      selectedStandardAttrs.has(attr.key)
                        ? styles.attrSelected
                        : styles.attrUnselected
                    } ${attr.required ? styles.attrDisabled : styles.attrHover}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedStandardAttrs.has(attr.key)}
                      onChange={() => handleStandardAttrToggle(attr.key)}
                      disabled={attr.required}
                      className="mr-3 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <span className={styles.attrText}>
                        {attr.label}
                        {attr.required && (
                          <span className={`${styles.required} ml-1`}>*</span>
                        )}
                      </span>
                      <span className={styles.attrMeta}>({attr.type})</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Custom Attributes — available for every type in the spreadsheet wizard */}
            {loadingAttributes ? (
              <div className={styles.muted}>Loading custom attributes...</div>
            ) : customAttributes.length > 0 ? (
              <div className="mb-6">
                <h4 className={styles.subheading}>Custom Attributes</h4>
                <div className="space-y-2">
                  {customAttributes.map((attr) => (
                    <label
                      key={attr.attribute_name}
                      className={`flex items-center p-3 rounded-lg border ${
                        selectedCustomAttrs.has(attr.attribute_name)
                          ? styles.attrSelected
                          : styles.attrUnselected
                      } ${attr.is_required ? styles.attrDisabled : styles.attrHover}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCustomAttrs.has(attr.attribute_name)}
                        onChange={() =>
                          handleCustomAttrToggle(attr.attribute_name)
                        }
                        disabled={attr.is_required}
                        className="mr-3 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <span className={styles.attrText}>
                          {attr.attribute_label || attr.attribute_name}
                          {attr.is_required && (
                            <span className={`${styles.required} ml-1`}>*</span>
                          )}
                        </span>
                        <span className={styles.attrMeta}>
                          ({attr.data_type})
                        </span>
                        {attr.data_type === "date" && (
                          <div className={styles.attrDesc}>
                            Format: YYYY-MM-DD (e.g. 2026-09-10)
                          </div>
                        )}
                        {attr.data_type === "boolean" && (
                          <div className={styles.attrDesc}>
                            Allowed: TRUE or FALSE
                          </div>
                        )}
                        {(attr.data_type === "select" ||
                          attr.data_type === "multi_select") &&
                          Array.isArray(attr.validation_rules?.options) &&
                          attr.validation_rules.options.length > 0 && (
                            <div className={styles.attrDesc}>
                              {attr.data_type === "multi_select"
                                ? "Allowed values (separate with ; ): "
                                : "Allowed values: "}
                              {attr.validation_rules.options.join("; ")}
                            </div>
                          )}
                        {attr.description && (
                          <div className={styles.attrDesc}>
                            {attr.description}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ) : selectedEntityType ? (
              <div className={styles.muted}>{copy.noCustomAttrs}</div>
            ) : null}

            {templateGenerated && (
              <div className={styles.alertSuccess}>
                <p className={styles.alertSuccessText}>
                  {copy.templateReadyNote}
                </p>
              </div>
            )}
            <div className={styles.alertInfo}>
              <p className={styles.alertInfoText}>{copy.fieldFormatNote}</p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                className={styles.btnSecondary}
              >
                Back
              </button>
              {!templateGenerated ? (
                <LoadingButton
                  onClick={generateTemplate}
                  loading={loading}
                  loadingLabel="Generating..."
                  disabled={
                    selectedStandardAttrs.size === 0 ||
                    !selectedEntityType
                  }
                  className={styles.btnPrimary}
                >
                  Generate Template
                </LoadingButton>
              ) : (
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  className={styles.btnPrimary}
                >
                  Continue to Upload
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Upload File */}
        {step === 3 && (
          <div className="space-y-4">
            <h3 className={styles.stepTitle}>Step 3: Upload Populated File</h3>
            <div className={styles.alertInfo}>
              <p className={styles.alertInfoText}>{copy.fieldFormatNote}</p>
            </div>
            {savedConfig && (
              <div className={styles.alertInfo}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className={styles.alertInfoTitle}>
                      Using saved configuration
                    </p>
                    <p className={styles.alertInfoText}>
                      {copy.typePrefix}{" "}
                      <span className="font-semibold">
                        {savedConfig.entityType}
                      </span>{" "}
                      | Import Type:{" "}
                      <span className="font-semibold">
                        {savedConfig.importType.toUpperCase()}
                      </span>
                    </p>
                    <div className="flex gap-2 mt-3">
                      <LoadingButton
                        onClick={async () => {
                          try {
                            setLoadingDownload(true);
                            setError("");

                            await downloadTemplateFile({
                              import_type: savedConfig.importType,
                              entity_type: savedConfig.entityType,
                              attributes: {
                                standard: savedConfig.standardAttrs,
                                custom: savedConfig.customAttrs,
                              },
                            });
                          } catch (error) {
                            debugError("Error downloading template:", error);
                            setError(
                              "Failed to download template: " + error.message
                            );
                          } finally {
                            setLoadingDownload(false);
                          }
                        }}
                        loading={loadingDownload}
                        loadingLabel="Downloading..."
                        className={styles.btnSmPrimary}
                      >
                        Download Template Again
                      </LoadingButton>
                      <button
                        type="button"
                        onClick={() => {
                          clearSavedImportConfig();
                          setSelectedEntityType("");
                          setImportType("");
                          setSelectedStandardAttrs(new Set(["name"]));
                          setSelectedCustomAttrs(new Set());
                        }}
                        className={styles.btnSmDanger}
                      >
                        Delete Configuration
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {!selectedEntityType && savedConfig && (
              <div className={styles.alertWarning}>
                <p className={styles.alertWarningText}>
                  {copy.noTypeSelected}
                </p>
                <button
                  type="button"
                  onClick={useSavedConfigForUpload}
                  className={v2cx(styles.btnSmWarning, "mt-2")}
                >
                  Use Saved Config
                </button>
              </div>
            )}
            <div>
              <label className={styles.label}>Select File</label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className={styles.select}
              />
              {uploadedFile && (
                <div className={styles.selectedFile}>
                  Selected: {uploadedFile.name}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setStep(2);
                  setUploadedFile(null);
                }}
                className={styles.btnSecondary}
              >
                Back
              </button>
              <LoadingButton
                onClick={processImport}
                loading={loading}
                loadingLabel="Processing..."
                disabled={!uploadedFile}
                className={styles.btnPrimary}
              >
                {copy.importButton}
              </LoadingButton>
            </div>

            {/* Import Status Display */}
            {importStatus && (
              <div className={styles.statusPanel}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className={styles.statusTitle}>Import Status</h4>
                  {importJobId && (
                    <span className={styles.statusMeta}>
                      Job ID: {importJobId.substring(0, 8)}...
                    </span>
                  )}
                </div>
                {importStatus.status === "queued" && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className={styles.spinner} />
                      <p className={styles.statusSuccess}>
                        Import job queued successfully
                      </p>
                    </div>
                    {importStatus.message && (
                      <p className={v2cx(styles.statusSubtext, "ml-6")}>
                        {importStatus.message}
                      </p>
                    )}
                    <p className={v2cx(styles.statusSubtext, "ml-6")}>
                      You can close this dialog; the import will continue in the
                      background.
                    </p>
                  </div>
                )}
                {importStatus.status === "pending" && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className={styles.spinner} />
                      <p className={styles.statusText}>Waiting to process...</p>
                    </div>
                    <p className={v2cx(styles.statusSubtext, "ml-6")}>
                      You can close this dialog; the import will continue in the
                      background.
                    </p>
                  </div>
                )}
                {importStatus.status === "running" && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className={styles.spinner} />
                      <p className={styles.statusText}>
                        Processing import file...
                      </p>
                    </div>
                    <p className={v2cx(styles.statusSubtext, "ml-6")}>
                      You can close this dialog; the import will continue in the
                      background.
                    </p>
                  </div>
                )}
                {importStatus.status === "completed" && importStatus.result && (
                  <div className="space-y-2">
                    <p className={styles.statusSuccess}>Import completed</p>
                    <p className={styles.statusSubtext}>
                      Successful: {importStatus.result.successful || 0} / Total:{" "}
                      {importStatus.result.total || 0}
                    </p>
                  </div>
                )}
                {importStatus.status === "failed" && (
                  <div>
                    <p className={styles.statusError}>Import failed</p>
                    {importStatus.error && (
                      <p className={v2cx(styles.statusSubtext, "mt-1")}>
                        {importStatus.error}
                      </p>
                    )}
                  </div>
                )}
                {(importStatus.status === "queued" ||
                  importStatus.status === "pending" ||
                  importStatus.status === "running") && (
                  <div className={styles.statusDivider}>
                    <button
                      type="button"
                      onClick={onClose}
                      className={styles.btnSecondary}
                    >
                      Close and continue working
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 4: Import Results */}
        {step === 4 && importResults && (
          <div className="space-y-4">
            <h3 className={styles.stepTitle}>Import Results</h3>
            <div className="space-y-3">
              <div className={styles.resultTotal}>
                <p className={styles.resultTotalText}>
                  Total Processed: {importResults.total || 0}
                </p>
              </div>
              {importResults.successful > 0 && (
                <div className={styles.resultSuccess}>
                  <p className={styles.resultSuccessText}>
                    Successful: {importResults.successful}
                  </p>
                </div>
              )}
              {importResults.failed > 0 && (
                <div className={styles.resultFailed}>
                  <p className={styles.resultFailedText}>
                    Failed: {importResults.failed}
                  </p>
                  {importResults.errors && importResults.errors.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {importResults.errors.slice(0, 10).map((error, idx) => (
                        <div key={idx} className={styles.resultErrorItem}>
                          • {error}
                        </div>
                      ))}
                      {importResults.errors.length > 10 && (
                        <div className={styles.resultErrorItem}>
                          ... and {importResults.errors.length - 10} more errors
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={resetWizard}
                className={styles.btnSecondary}
              >
                Import More
              </button>
              <button
                type="button"
                onClick={onClose}
                className={styles.btnPrimary}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EntityImportWizard;
